import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PlusCircle, Users, Clock, HandCoins, ArrowRight, Sprout, ChevronLeft } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const D = { navy: "#1e2130", surface: "#292d44", coral: "#f76359", cream: "#faf6f0" };

export default function CuratorDashboard() {
  const nav = useNavigate();
  const { user, ready } = useAuth();
  const [groups, setGroups] = useState(null);

  useEffect(() => {
    if (!ready) return;
    if (!user) { nav("/auth"); return; }
    if (user.tier !== "curator" && user.role !== "admin") { nav("/upgrade"); return; }
    api.get("/curator/groups").then(({ data }) => setGroups(data.groups)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [ready, user]); // eslint-disable-line

  const eligible = (groups || []).filter((g) => g.ministry_eligible);

  return (
    <div style={{ background: D.navy, color: D.cream }} className="min-h-screen">
      <div className="mx-auto max-w-6xl px-5 py-6 sm:px-8">
        <button onClick={() => nav("/plaza")} className="mb-3 flex items-center gap-1 text-sm text-[#faf6f0]/60 hover:text-white">
          <ChevronLeft size={16} /> Back to the Plaza
        </button>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-[#f76359]">Seed of future ministries</p>
            <h1 className="font-extrabold tracking-tight text-[#faf6f0]" style={{ fontSize: "clamp(2rem,5vw,44px)" }}>Curator back office</h1>
            <p className="mt-1 text-sm text-[#faf6f0]/70">Grow a table into a movement. Everything your groups need, in one place.</p>
          </div>
          <button onClick={() => nav("/host/new")} data-testid="curator-new-group"
            className="inline-flex items-center gap-2 rounded-full bg-[#f76359] px-5 py-3 text-sm font-semibold text-white hover:bg-[#e2554c]">
            <PlusCircle size={17} /> New group
          </button>
        </div>

        {groups === null ? (
          <p className="mt-10 text-[#faf6f0]/60">Loading your groups…</p>
        ) : groups.length === 0 ? (
          <div className="mt-8 rounded-2xl border border-dashed border-[#faf6f0]/20 p-10 text-center text-[#faf6f0]/70">
            No groups yet. Create your first public study.
          </div>
        ) : (
          <div className="mt-8 grid gap-4 md:grid-cols-2">
            {groups.map((g) => (
              <button key={g.id} onClick={() => nav(`/curator/${g.id}`)} data-testid={`curator-group-${g.id}`}
                className="rounded-[22px] p-5 text-left transition-transform hover:-translate-y-0.5" style={{ background: D.surface }}>
                <div className="flex items-center justify-between">
                  <span className="rounded-full bg-[#f76359]/20 px-2.5 py-1 text-xs font-bold text-[#f76359]">{g.role}</span>
                  {g.pending > 0 && <span className="rounded-full bg-amber-400/20 px-2.5 py-1 text-xs font-semibold text-amber-300">{g.pending} to approve</span>}
                </div>
                <h3 className="mt-3 text-lg font-bold text-[#faf6f0]">{g.title}</h3>
                <p className="text-sm text-[#faf6f0]/60">{g.venue.name} · {g.schedule.day_name}s {g.schedule.start_time}</p>
                <div className="mt-4 flex items-center gap-5 text-sm text-[#faf6f0]/80">
                  <span className="flex items-center gap-1.5"><Users size={15} className="text-[#f76359]" /> {g.confirmed}{g.capacity ? `/${g.capacity}` : ""}{g.waitlist ? ` (+${g.waitlist})` : ""}</span>
                  <span className="flex items-center gap-1.5"><HandCoins size={15} className="text-[#f76359]" /> ${(g.raised / 100).toFixed(0)}{g.donation_goal ? ` / $${g.donation_goal}` : ""}</span>
                  <span className={`ml-auto flex items-center gap-1 text-xs font-semibold ${g.active ? "text-emerald-300" : "text-amber-300"}`}>{g.active ? "Active" : "Needs members"}</span>
                </div>
              </button>
            ))}
          </div>
        )}

        {eligible.length > 0 && (
          <div className="mt-8 rounded-[22px] border border-[#f76359]/40 p-6" style={{ background: D.surface }}>
            <div className="flex items-center gap-2 text-[#f76359]"><Sprout size={18} /><span className="font-bold">Ministry track unlocked</span></div>
            <p className="mt-1 text-sm text-[#faf6f0]/75">
              {eligible.map((g) => g.title).join(", ")} {eligible.length === 1 ? "has" : "have"} grown large and consistent.
              Group multiple studies under one ministry with its own page and leader dashboard.
            </p>
            <button onClick={() => nav("/curator/ministry/new")} data-testid="create-ministry"
              className="mt-3 inline-flex items-center gap-2 rounded-full border border-[#f76359] px-4 py-2 text-sm font-semibold text-[#f76359] hover:bg-[#f76359]/10">
              Start a ministry <ArrowRight size={15} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
